/**
 * Trial telemetry for setting a job up (telemetry catalog §5.6, docs/architecture/telemetry-catalog.md): which stage
 * the setup screen shows and how long each took, and whether its counts have stopped moving. Pure, so the telemetry
 * tests check it without a window. Code words, counts and times only: never a type, field, file or folder name.
 */

/** The setup stages (`setup.stage`), in order: the setup screen's five learning parts, then reading every file. */
export const setupStageCodes = [
  'reading',
  'separating',
  'types',
  'fields',
  'values',
  'files',
] as const;
export type SetupStageCode = (typeof setupStageCodes)[number];

/**
 * The stage a setup screen shows: its learning part (0 Reading pages … 4 Reading your documents, setup-journey-model.ts),
 * saving what it learned as the end of the last part, and reading every file once setup is saved.
 */
export function setupStageOf(
  part: number,
  phase: 'learning' | 'saving' | 'reading' | 'ready',
): SetupStageCode {
  if (phase === 'reading' || phase === 'ready') return 'files';
  return setupStageCodes[Math.max(0, Math.min(4, Number.isFinite(part) ? Math.floor(part) : 0))];
}

/** The stage being timed: which one, and since when. */
export type SetupStageWatch = { stage: SetupStageCode; since: number } | null;

/**
 * One `setup.stage` per stage left: when the stage shown moves on, the stage it leaves with its time. The screen
 * never goes back (setup-progress-floor.ts), so a stage earlier than the one timed is ignored.
 */
export function advanceSetupStage(
  previous: SetupStageWatch,
  stage: SetupStageCode,
  now: number,
): { watch: SetupStageWatch; finished: { stage: SetupStageCode; ms: number } | null } {
  if (!previous) return { watch: { stage, since: now }, finished: null };
  if (setupStageCodes.indexOf(stage) <= setupStageCodes.indexOf(previous.stage))
    return { watch: previous, finished: null };
  return {
    watch: { stage, since: now },
    finished: { stage: previous.stage, ms: Math.max(0, now - previous.since) },
  };
}

/**
 * Waiting with nothing moving: the screen's counts (pages, documents, the step's own count) as one key, and how long
 * they have stood still while the window was in front.
 */
export type NoProgressWatch = { key: string; quietMs: number; lastAt: number; fired: boolean };

/** The longest gap between two looks that still counts as waiting time (a sleeping computer is not waiting). */
const longestCountedGapMs = 10_000;

/**
 * `reading.no-progress` once when the counts have not moved for `thresholdMs` of visible time
 * (telemetryStuckThresholds.noProgressMs), and not again until they move. Time with the window hidden, or a gap
 * longer than a few seconds between looks (the computer asleep), is not counted.
 */
export function advanceNoProgress(
  previous: NoProgressWatch | null,
  key: string,
  now: number,
  visible: boolean,
  thresholdMs: number,
): { watch: NoProgressWatch; minutes: number | null } {
  if (!previous || previous.key !== key)
    return { watch: { key, quietMs: 0, lastAt: now, fired: false }, minutes: null };
  const gap = now - previous.lastAt;
  const quietMs = previous.quietMs + (visible && gap > 0 && gap <= longestCountedGapMs ? gap : 0);
  const watch = { key, quietMs, lastAt: Math.max(previous.lastAt, now), fired: previous.fired };
  if (watch.fired || quietMs < thresholdMs) return { watch, minutes: null };
  return { watch: { ...watch, fired: true }, minutes: Math.round(quietMs / 60_000) };
}

/** How a setup's wizard handed over to the job (`setup.handoff`). */
export type SetupHandoffHow = 'start-checking' | 'stayed' | 'auto' | 'closed';

/**
 * Start checking pressed; the wizard closed; or it opened the job by itself when its documents were done, after
 * offering Start checking (stayed) or before it could (auto).
 */
export function setupHandoffHow(
  by: 'button' | 'close' | 'done',
  offered: boolean,
): SetupHandoffHow {
  if (by === 'button') return 'start-checking';
  if (by === 'close') return 'closed';
  return offered ? 'stayed' : 'auto';
}
